import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { LOCAL_VAR } from './../constants';
import { LocalStorageService } from '@app/core/local-storage.service';
// import { USER_TYPES } from '@app/core/constants';

const credentialsKey = LOCAL_VAR.credentialsKey;
const selectedAgentKey = LOCAL_VAR.selectedAgent;

/**
 * Provides a base for authentication workflow.
 * The Credentials interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class AuthenticationService {
  private _credentials: Authentication.Credentials | null;
  private _merchant: Authentication.Merchant | null;
  public merchant$ = new EventEmitter<Authentication.Merchant>();
  public credentials$ = new EventEmitter<Authentication.Credentials>();
  private _liveMode = false;
  public liveMode$ = new EventEmitter<boolean>();
  // private _merchants: Authentication.Merchant | null;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService
  ) {
    const savedCredentials = this.localStorageService.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }

    /*** Set new merchant in existing merchants ***/
    if (this.isAuthenticated()) {
      // this.refresh();
    }
  }

  refresh() {
    setTimeout(() => {
      this.me().subscribe(
        (user: Authentication.User) => {
          this._credentials.user = user;
          this.setCredentials(this._credentials);
        },
        (error: any) => {
          console.log(`get merchants error: ${error}`);
        }
      );
    }, 1);
  }

  me(): Observable<Authentication.User> {
    return this.httpClient.get('/auth/me').pipe(
      map((body: Authentication.User) => {
        return body;
      })
    );
  }

  login(payload: any): Observable<any> {
    return this.httpClient.post('/auth/login', payload).pipe(
      map((body: any) => {
        // OTP challenge — do not store session credentials yet
        if (body && body.otp === 1) {
          return body;
        }
        this.setCredentials(body);
        return body;
      })
    );
  }

  verifyLoginOtp(payload: { loginToken: string; otp: string }): Observable<Authentication.Credentials> {
    return this.httpClient.post('/auth/login', payload).pipe(
      map((body: Authentication.Credentials) => {
        this.setCredentials(body);
        return body;
      })
    );
  }

  resendLoginOtp(payload: { loginToken: string }): Observable<any> {
    return this.httpClient.post('/auth/resendotp', payload).pipe(
      map((body: any) => {
        return body;
      })
    );
  }

  setPassword(payload: any): Observable<Authentication.Credentials> {
    return this.httpClient.post('/auth/activation', payload).pipe(
      map((body: any) => {
        return body;
      })
    );
  }

  forgotPassword(payload: any): Observable<Authentication.Credentials> {
    return this.httpClient.post('/auth/forgotpassword', payload).pipe(
      map((body: any) => {
        return body;
      })
    );
  }

  resetPassword(payload: any): Observable<Authentication.Credentials> {
    return this.httpClient.post('/auth/updatepassword', payload).pipe(
      map((body: any) => {
        return body;
      })
    );
  }

  signup(
    payload: Authentication.SignupPayload
  ): Observable<Authentication.User> {
    return this.httpClient.post('/signup', payload).pipe(
      map((body: Authentication.User) => {
        return body;
      })
    );
  }

  verifyEmail(emailHash: string): Observable<Authentication.User> {
    return this.httpClient.get(`/verifyEmail?emailHash=${emailHash}`).pipe(
      map((body: Authentication.User) => {
        return body;
      })
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return {Observable<boolean>} True if the user was logged out successfully.
   */
  logout(flag?: boolean): Observable<boolean> {
    if (flag) {
      this.setCredentials();
      return of(true);
    } else {
      return this.httpClient
        .post('/logout', {
          sessionId: this.credentials.session
        })
        .pipe(
          map(() => {
            this.setCredentials();
            return true;
          })
        );
    }
  }

  /**
   * Clears in-memory session when dowc_credentials is missing from storage.
   */
  private syncCredentialsWithStorage(): void {
    const saved = this.localStorageService.getItem(credentialsKey);
    if (!saved) {
      this._credentials = null;
    }
  }

  /**
   * Checks is the user is authenticated.
   * Requires both in-memory credentials and dowc_credentials in localStorage.
   * @return {boolean} True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    this.syncCredentialsWithStorage();
    return !!this._credentials;
  }

  uploadPhoto(payload: any): Observable<any> {
    return this.httpClient.put('/auth/uploadFile', payload).pipe(
      map((body: any) => {
        return body;
      })
    );
  }

  /**
   * Gets the user credentials.
   * @return {Credentials} The user credentials or null if the user is not authenticated.
   */
  get credentials(): Authentication.Credentials | null {
    this.syncCredentialsWithStorage();
    return this._credentials;
  }

  /**
   * Get the auth token.
   * @return {string} The auth token is null if user is not authenticated.
   */
  get accessToken(): string | null {
    return this.credentials ? this.credentials.accessToken : null;
  }

  /**
   * Get the auth token.
   * @return {string} The auth token is null if user is not authenticated.
   */
  get merchant(): Authentication.Merchant | null {
    return this._merchant ? this._merchant : null;
  }

  /**
   * Get the auth token.
   * @return {string} The auth token is null if user is not authenticated.
   */
  get sessionId(): string | null {
    return this.credentials ? this.credentials.session : null;
  }

  /**
   * Sets the user credentials.
   * @param {Credentials=} Authentication.Credentials The user credentials.
   */
  private setCredentials(credentials?: Authentication.Credentials) {
    this._credentials = credentials || null;
    if (credentials) {
      this.localStorageService.setItem(
        credentialsKey,
        JSON.stringify(credentials)
      );
      this.credentials$.emit(this._credentials);
      if (
        this.credentials.user.userRoleId.slug === 'agentuser' &&
        this.credentials.user.userId !== undefined
      ) {
        this.localStorageService.setItem(
          selectedAgentKey,
          JSON.stringify(this.credentials.user.agentData)
        );
      } else if (this.credentials.user.userRoleId.slug !== 'admin') {
        this.localStorageService.setItem(
          selectedAgentKey,
          JSON.stringify(this.credentials.user)
        );
      }
    } else {
      this.localStorageService.clearItem(credentialsKey);
    }
  }

  /**
   * Get Live mode value
   * @return {boolean} True if livemode enabled
   */
  get liveMode(): boolean {
    return this._liveMode;
  }

  /**
   * Set Live mode value
   * @liveMode {boolean}
   */
  set liveMode(liveMode: boolean) {
    this._liveMode = liveMode;
    this.liveMode$.emit(this.liveMode);
  }

  get loggedInUserType() {
    if (
      this.credentials &&
      this.credentials.user &&
      this.credentials.user.userRoleId &&
      this.credentials.user.userRoleId.slug
    ) {
      return this.credentials.user.userRoleId.slug;
    } else {
      return '';
    }
  }
}
